import type { JSX } from 'react';
import type { FeedbackPalette, WidgetStep } from '../types.js';

interface TypePickerStepProps {
  palette: FeedbackPalette;
  step: WidgetStep;
  selectedType: string | null;
  onSelect: (key: string) => void;
}

const DEFAULT_TYPE_OPTIONS: { key: string; label: string; icon: string }[] = [
  { key: 'bug', label: 'Bug', icon: '🐛' },
  { key: 'feature', label: 'Feature request', icon: '✨' },
  { key: 'question', label: 'Question', icon: '❓' },
  { key: 'other', label: 'Other', icon: '💬' },
];

// Map icon name strings (e.g. "bug", "sparkles") to emoji glyphs so the SDK
// can render team-configured icons without bundling an icon library.
const ICON_NAME_TO_EMOJI: Record<string, string> = {
  bug: '🐛',
  sparkles: '✨',
  lightbulb: '💡',
  help: '❓',
  question: '❓',
  chat: '💬',
  message: '💬',
  feedback: '💬',
  star: '⭐',
  smile: '😊',
  link: '🔗',
  palette: '🎨',
  feature: '✨',
  'feature-request': '✨',
  suggestion: '🧠',
  improvement: '✨',
};

function resolveIcon(icon?: string): string {
  if (!icon) return '💬';
  // If already an emoji (non-ASCII char) just use it directly.
  if (/[^\x00-\x7F]/.test(icon)) return icon;
  const key = icon.toLowerCase().trim();
  return ICON_NAME_TO_EMOJI[key] || '💬';
}

export function TypePickerStep({ palette, step, selectedType, onSelect }: TypePickerStepProps): JSX.Element {
  const options = step.options ?? DEFAULT_TYPE_OPTIONS;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
      {options.map((option) => {
        const selected = selectedType === option.key;
        const glyph = resolveIcon(option.icon);
        return (
          <button
            key={option.key}
            type="button"
            onClick={() => onSelect(option.key)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              padding: '16px 10px',
              borderRadius: palette.radiusLg,
              border: `1.5px solid ${selected ? palette.primary : palette.border}`,
              background: selected ? palette.primarySoft : palette.card,
              color: palette.text,
              cursor: 'pointer',
              transition: 'all 160ms ease',
            }}
          >
            <span style={{ fontSize: 24, lineHeight: 1 }}>{glyph}</span>
            <span style={{ fontSize: 14, fontWeight: 500 }}>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
